import { QuisGroup } from '@/components/ConversationWithTutor/MessageItem/MessageItem';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from './QuiFromDashboard.module.scss';
import classes from 'classnames';
import { useAppSelector } from '@/store';
import { studyingLanguageSelector } from '@/store/reducers/auth';
import SVG from 'react-inlinesvg';
import questionIcon from '@/assets/svg/question.svg';
import correct from '@/assets/svg/correct-circle.svg';
import wrong from '@/assets/svg/wrong-circle.svg';
import { useTranslation } from 'react-i18next';

export interface QuiFromDashboardProps {
  quisGroups: QuisGroup[];
  onQuizComplete?: (correctAnswers: number, numberOfQuestions: number) => void;
}

const QuiFromDashboard = ({ quisGroups, onQuizComplete }: QuiFromDashboardProps) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const studyingLanguage = useAppSelector(studyingLanguageSelector);
  const [pickedAnswer, setPickedAnswer] = useState('');
  const [answerStatus, setAnswerStatus] = useState<'correct' | 'wrong' | 'pending'>('pending');
  const correctAnswers = useRef<number>(0);
  const { t } = useTranslation();

  useEffect(() => {
    correctAnswers.current = 0;
  }, [quisGroups]);

  const currentQuestion = useMemo(
    () => quisGroups[currentQuestionIndex] || {},
    [currentQuestionIndex, quisGroups]
  );

  const wordsArray =
    currentQuestion?.question
      ?.replace(/_+/g, '_ ')
      ?.split(/\s+/)
      ?.map(word => word.replace(/_+(?=\W)/g, '').replace(/_+/g, '')) || [];

  const onSelect = (option: string) => {
    if (!pickedAnswer) {
      const isCorrect = option.toLowerCase() === currentQuestion?.correctAnswer?.toLowerCase();
      setPickedAnswer(option);
      setAnswerStatus(isCorrect ? 'correct' : 'wrong');

      if (isCorrect) {
        correctAnswers.current += 1;
      }

      setTimeout(() => {
        if (currentQuestionIndex < quisGroups.length - 1) {
          setCurrentQuestionIndex(prevIndex => prevIndex + 1);
          setPickedAnswer('');
          setAnswerStatus('pending');
        } else {
          onQuizComplete?.(correctAnswers.current, quisGroups.length);
        }
      }, 3000);
    }
  };

  const multiChoiceQuestionComponent = useMemo(
    () => (
      <div className={classNames.questionItem}>
        <SVG src={questionIcon} />
        <div className={classNames.text}>
          {wordsArray.map((part, index) => {
            const isBlank = part === '';
            let content = <span className={classNames.textPart}>{part}</span>;
            if (isBlank) {
              content = (
                <span
                  className={classes(classNames.specialFill, {
                    [classNames.specialFillFilled]: pickedAnswer,
                  })}
                >
                  {(pickedAnswer && currentQuestion?.correctAnswer) || '   '}
                </span>
              );
            }
            return <React.Fragment key={index}>{content} </React.Fragment>;
          })}
        </div>
      </div>
    ),
    [pickedAnswer, wordsArray, studyingLanguage]
  );

  const optionsElements = currentQuestion?.answers?.map((option, index) => {
    const isSelected = option === pickedAnswer;
    const isCorrect = answerStatus === 'correct' && isSelected;
    const isWrong = answerStatus === 'wrong' && isSelected;

    const optionClasses = classes(classNames.option, {
      'cursor-pointer': !pickedAnswer,
      [classNames.optionCorrect]: isCorrect,
      [classNames.optionWrong]: isWrong,
    });
    const alphabet = String.fromCharCode(index + 97);

    return (
      <div key={index} className={optionClasses} onClick={() => onSelect(option)}>
        {!isSelected ? (
          <span className={classNames.alphabet}>{alphabet}</span>
        ) : (
          <div className={classNames.answerStatusImgWrapper}>
            <SVG src={isCorrect ? correct : wrong} className={classNames.answerStatusImg} />
          </div>
        )}
        <div className={classNames.text}>{option}</div>
      </div>
    );
  });

  return (
    <>
      <div className={classNames.quiz}>
        <div className={classNames.imagesSideBlockInstructions}>{t('chooseTheCorrectAnswer')}</div>
        <div className={classNames.multiChoiceQuestion}>{multiChoiceQuestionComponent}</div>
        <div className={classNames.multiChoiceOptions}>{optionsElements}</div>
      </div>
      <div className={classNames.numQuestions}>
        {`${currentQuestionIndex + 1}/${quisGroups.length} questions`}
      </div>
    </>
  );
};

export default QuiFromDashboard;
